import axios from 'axios'
import type { AxiosError } from 'axios'
import { jwtDecode } from 'jwt-decode'
import { toaster } from '@/shared/ui'
import { config } from '@/shared/config'
import { useAuthStore } from '@/shared/store/authStore'
import type { IAuthUser } from '@/shared/types/auth-user'

/** Maps the app's stored locale key to the correct Accept-Language header value. */
const LOCALE_TO_ACCEPT_LANGUAGE: Record<string, string> = {
  ru: 'ru',
  uz: 'uz-Latn',
  'uz-Cyrl': 'uz-Cyrl',
}

function getAcceptLanguage(): string {
  const appLng = localStorage.getItem('appLng') ?? 'ru'
  return LOCALE_TO_ACCEPT_LANGUAGE[appLng] ?? 'ru'
}

export const HTTP_MESSAGES = (status: number) => {
  const appLng = localStorage.getItem('appLng') ?? 'ru'
  const isRu = appLng === 'ru'
  switch (status) {
    case 401:
      return isRu
        ? 'Время доступа к учетной записи истекло. Пожалуйста, войдите снова.'
        : 'Akkountga kirish vaqti tugadi. Qaytadan kiring.'
    case 403:
      return isRu
        ? 'У вас нет доступа к этой странице.'
        : "Foydalanuvchiga kirish huquqi yo'q."
    case 404:
      return isRu ? 'Страница не найдена.' : 'Sahifa topilmadi.'
    case 409:
      return isRu ? 'Конфликт данных.' : "Ma'lumotlar konflikti."
    case 417:
      return isRu
        ? 'У вас нет доступа к этой странице.'
        : "Foydalanuvchiga kirish huquqi yo'q."
    default:
      return isRu ? 'Произошла неизвестная ошибка.' : 'Xatolik yuz berdi.'
  }
}

/**
 * Auth lives on the main gateway only — the service instances (visit-engine,
 * production-core) must not resolve `/auth/refresh` against their own root.
 */
const AUTH_REFRESH_URL = `${config.BASE_URI}/auth/refresh`

// One refresh in flight for every instance and the route guard: concurrent
// callers must wait on it, not each spend the same refresh token.
let refreshInFlight: Promise<string> | null = null

/**
 * Trades the refresh token for a new pair and re-derives the stored user from
 * the new access token, so `user.exp` follows the token actually in use.
 * Throws when there is no refresh token or the gateway refuses it.
 */
export function refreshTokens(): Promise<string> {
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async () => {
    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) throw new Error('No refresh token')

    const { data } = await axios.post(AUTH_REFRESH_URL, { refreshToken })
    const { access_token: accessToken, refresh_token: newRefreshToken } = data
    if (!accessToken) throw new Error('No access token in response')

    localStorage.setItem('access_token', accessToken)
    if (newRefreshToken) localStorage.setItem('refresh_token', newRefreshToken)
    useAuthStore.getState().setUser(jwtDecode<IAuthUser>(accessToken))
    return accessToken as string
  })().finally(() => {
    refreshInFlight = null
  })

  return refreshInFlight
}

function createAxiosInstance(baseURL: string) {
  const instance = axios.create({
    baseURL,
  })

  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem('access_token')

    config.headers['Accept-Language'] = getAcceptLanguage()

    // Refresh token endpoint'i uchun refresh token'ni Authorization Bearer formatida yuborish
    if (config.url?.includes('/auth/refresh')) {
      const refreshToken = localStorage.getItem('refresh_token')
      if (refreshToken) {
        config.headers.Authorization = `Bearer ${refreshToken}`
      }
    } else if (token) {
      // Boshqa request'lar uchun access token'ni yuborish
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  })

  instance.interceptors.response.use(
    (response) => {
      return response
    },
    async (error: AxiosError) => {
      const status = error.response?.status

      const originalRequest = error.config as any
      if (status === 401 && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true
        try {
          const accessToken = await refreshTokens()
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
          return instance(originalRequest)
        } catch {
          toaster.error({
            title: HTTP_MESSAGES(401),
          })
          localStorage.clear()
          window.location.href = '/auth/entry'
          return Promise.reject(error)
        }
      }
      // 401/403: show toast immediately before redirecting (full-page nav loses the toast otherwise)
      if (status === 401) {
        toaster.error({
          title: HTTP_MESSAGES(status),
        })
        localStorage.clear()
        window.location.href = '/auth/entry'
      }

      return Promise.reject(error)
    },
  )

  return instance
}

export const api = createAxiosInstance(config.BASE_URI || 'api')
export const apiVisitEngine = createAxiosInstance(
  config.BASE_URL_VISIT_ENGINE || 'api',
)
export const apiProductionCore = createAxiosInstance(
  config.BASE_URI + '/service/production-core/api' || 'api',
)
